import React, { useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPreOrderLayoutWrapper from "../../components/ProjectPreOrderLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaArrowLeft, FaPlus, FaPaperPlane } from "react-icons/fa";

const BASE_URL = import.meta?.env?.VITE_BASE_URL || "https://nissi-india.com";

const ProjectFileManager = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const location = useLocation();

  // query params
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const contractId = query.get("contractId");
  const tenderId = query.get("tenderId");

  // localStorage helper
  const getLS = (key) => {
    try {
      return localStorage.getItem(key) || "";
    } catch {
      return "";
    }
  };

  // localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchId = getLS("branchId") || getLS("branchid");
  const branchType = getLS("branchType") || getLS("branchtype");
  const userId = getLS("userId") || getLS("loginuserid");

  const branchid = Number(branchId || 0);

  // common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branchtype": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId],
  );

  const [showSupplierGrid, setShowSupplierGrid] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState({});
  const [suppliers, setSuppliers] = useState([]);

  const handleBack = () => {
    navigate(-1);
    toast.info("Going back...");
  };

  // load existing suppliers + docs
  useEffect(() => {
    if (!contractId || !tenderId) return;

    const fetchDetails = async () => {
      try {
        const res = await axios.get(`${BASE_URL}/api/tenderSupplier/details`, {
          params: { contractId, tenderId, branchid },
          headers: commonHeaders,
        });

        const data = Array.isArray(res.data)
          ? res.data
          : Array.isArray(res.data?.data)
            ? res.data.data
            : [];

        if (data.length > 0) {
          const fixed = data.map((r) => ({
            supplierId: r.supplierId || 0,
            supplierName: r.supplierName || "",
            files: Array.isArray(r.files) ? r.files : [],
          }));
          setSuppliers(fixed);
          setShowSupplierGrid(true);
        }
      } catch (err) {
        console.error(err);
        toast.error("Failed to load supplier documents");
      }
    };

    fetchDetails();
  }, [contractId, tenderId, branchid, commonHeaders]);

  const handleFilePick = (rowIndex, file) => {
    setSelectedFiles((prev) => ({ ...prev, [rowIndex]: file }));
  };

  // upload per row
  const uploadFileForRow = async (rowIndex) => {
    try {
      const file = selectedFiles[rowIndex];
      if (!file) {
        toast.warning("Please choose a file first");
        return;
      }

      const fd = new FormData();
      fd.append("file", file);
      fd.append("contractId", contractId || "");
      fd.append("tenderId", tenderId || "");
      fd.append("branchid", branchid || 0);

      const res = await axios.post(
        `${BASE_URL}/api/tenderSupplier/upload`,
        fd,
        {
          headers: {
            ...commonHeaders,
            "Content-Type": "multipart/form-data",
          },
        },
      );

      const filename = res.data?.filename || res.data?.data?.filename;
      if (!filename) throw new Error("Filename missing from server response");

      setSuppliers((prev) =>
        prev.map((s, i) => {
          if (i !== rowIndex) return s;
          const existing = Array.isArray(s.files) ? s.files : [];
          return { ...s, files: [...existing, filename] };
        }),
      );

      setSelectedFiles((prev) => {
        const copy = { ...prev };
        delete copy[rowIndex];
        return copy;
      });

      toast.success("File added");
    } catch (err) {
      console.error(err);
      toast.error("Upload failed");
    }
  };

  const openFile = (filename) => {
    window.open(`${BASE_URL}/TenderSupplier/${filename}`, "_blank");
  };

  const handleAddMore = () => {
    setShowSupplierGrid(true);
    setSuppliers((prev) => {
      if (prev.length === 0) {
        return [{ supplierId: 0, supplierName: "", files: [] }];
      }
      return [...prev, { supplierId: 0, supplierName: "", files: [] }];
    });
  };

  const updateSupplierName = (index, value) => {
    setSuppliers((prev) =>
      prev.map((s, i) => (i === index ? { ...s, supplierName: value } : s)),
    );
  };

  // submit
  const handleSubmit = async () => {
    try {
      if (!contractId || !tenderId) {
        toast.error("contractId / tenderId missing in URL");
        return;
      }

      const loginUserId = Number(userId || 0);
      if (!loginUserId) {
        toast.error("Login user id missing. Please re-login.");
        return;
      }

      const hasAnySupplier = suppliers.some(
        (s) => (s.supplierName || "").trim() !== "",
      );
      if (!hasAnySupplier) {
        toast.warning("Please add supplier records");
        return;
      }

      await axios.post(
        `${BASE_URL}/api/tenderSupplier/submit`,
        {
          contractId,
          tenderId,
          branchid,
          suppliers,
          loginUserId,
          finYearId,
          finYear,
          branchId,
          branchType,
          userId,
        },
        {
          headers: commonHeaders,
        },
      );

      toast.success("Saved Successfully");
      navigate(-1);
    } catch (err) {
      console.error(err);
      toast.error("Save failed");
    }
  };

  return (
    <ProjectPreOrderLayoutWrapper>
      <div
        className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}
      >
        <HeaderBar
          title={
            <span className="font-dmserif text-3xl">PROJECT FILE MANAGER</span>
          }
          setSkin={setSkin}
          currentSkin={skin}
        />
        <button
          onClick={handleBack}
          className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium rounded-xl border transition ${
            isDark
              ? "border-slate-600 text-slate-300 hover:bg-slate-800"
              : "border-slate-300 text-slate-700 hover:bg-slate-100"
          }`}
        >
          <FaArrowLeft />
          Back
        </button>

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <h1
              className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}
            >
              PROJECT FILE MANAGER
            </h1>
          </div>

          <div className="flex flex-col items-center justify-center min-h-96 space-y-8">
            {!showSupplierGrid ? (
              <div
                className={`w-full max-w-4xl p-8 rounded-xl border-2 border-dashed ${
                  isDark
                    ? "border-gray-600 bg-gray-800/50"
                    : "border-gray-300 bg-gray-50"
                }`}
              >
                <div className="text-center">
                  <div
                    className={`text-6xl mb-4 ${isDark ? "text-gray-500" : "text-gray-400"}`}
                  >
                    📁
                  </div>
                  <h3
                    className={`text-xl font-semibold mb-2 ${
                      isDark ? "text-gray-300" : "text-gray-600"
                    }`}
                  >
                    Project File Management Area
                  </h3>
                  <p
                    className={`text-sm ${isDark ? "text-gray-400" : "text-gray-500"}`}
                  >
                    Upload, organize, and manage your project files here
                  </p>
                </div>
              </div>
            ) : (
              <div className="w-full max-w-6xl space-y-4">
                {suppliers.map((s, idx) => (
                  <div
                    key={idx}
                    className={`p-4 rounded-xl border ${
                      isDark
                        ? "border-gray-700 bg-gray-800/50"
                        : "border-gray-200 bg-white"
                    }`}
                  >
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Supplier
                        </label>
                        <input
                          type="text"
                          value={s.supplierName}
                          onChange={(e) =>
                            updateSupplierName(idx, e.target.value)
                          }
                          className={`w-full px-3 py-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600 text-white"
                              : "bg-white border-gray-300 text-gray-900"
                          }`}
                        />
                      </div>

                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Add File
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            onChange={(e) =>
                              handleFilePick(idx, e.target.files?.[0] || null)
                            }
                            className={`w-full text-sm ${
                              isDark ? "text-gray-200" : "text-gray-700"
                            }`}
                          />
                          <button
                            type="button"
                            onClick={() => uploadFileForRow(idx)}
                            className={`shrink-0 px-4 py-2 rounded-lg text-sm font-semibold shadow ${
                              isDark
                                ? `${buttonClass} ${hoverClass} text-white`
                                : `${buttonClass} ${hoverClass} text-white`
                            }`}
                          >
                            Add
                          </button>
                        </div>
                      </div>

                      <div>
                        <label
                          className={`block text-sm font-medium mb-2 ${
                            isDark ? "text-gray-300" : "text-gray-700"
                          }`}
                        >
                          Files
                        </label>
                        <div
                          className={`min-h-12 p-2 rounded-lg border ${
                            isDark
                              ? "bg-gray-700 border-gray-600"
                              : "bg-white border-gray-300"
                          }`}
                        >
                          {s.files?.length ? (
                            <ul className="space-y-1">
                              {s.files.map((f, fIdx) => (
                                <li
                                  key={fIdx}
                                  onClick={() => openFile(f)}
                                  className={`text-xs underline cursor-pointer ${
                                    isDark
                                      ? "text-blue-300 hover:text-blue-200"
                                      : "text-blue-700 hover:text-blue-900"
                                  }`}
                                >
                                  {f}
                                </li>
                              ))}
                            </ul>
                          ) : (
                            <div
                              className={`text-xs ${isDark ? "text-gray-400" : "text-gray-500"}`}
                            >
                              No files added
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col items-center gap-4">
              <button
                onClick={handleAddMore}
                className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? `${buttonClass} ${hoverClass} text-white`
                    : `${buttonClass} ${hoverClass} text-white`
                }`}
              >
                <FaPlus /> Add More
              </button>

              {showSupplierGrid && (
                <button
                  onClick={handleSubmit}
                  className={`flex items-center gap-2 px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                    isDark
                      ? `${buttonClass} ${hoverClass} text-white`
                      : `${buttonClass} ${hoverClass} text-white`
                  }`}
                >
                  <FaPaperPlane /> Submit
                </button>
              )}
            </div>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPreOrderLayoutWrapper>
  );
};

export default ProjectFileManager;
